<script lang="ts">
	import { ProgressBar } from "misc-ui"
	import { onDestroy } from "svelte"

	interface Props {
		duration: number // total duration in seconds
		isActive: boolean // whether the break is currently active
		onBreakEnd: () => void // callback when break completes
	}

	let { duration, isActive, onBreakEnd }: Props = $props()

	let breakRemaining = $state(0)
	let breakInterval: number | null = null

	function clearBreakInterval() {
		if (breakInterval) {
			clearInterval(breakInterval)
			breakInterval = null
		}
	}

	function startBreak() {
		breakRemaining = duration
		const startTime = Date.now()
		const endTime = startTime + duration * 1000

		// Update immediately
		breakRemaining = Math.ceil((endTime - Date.now()) / 1000)

		// Clear any existing interval
		clearBreakInterval()

		breakInterval = window.setInterval(() => {
			const remaining = Math.ceil((endTime - Date.now()) / 1000)

			if (remaining <= 0) {
				clearBreakInterval()
				onBreakEnd()
			} else {
				breakRemaining = remaining
			}
		}, 100) // Check every 100ms for smoother countdown
	}

	// Start break when active
	$effect(() => {
		if (isActive) {
			startBreak()
		} else {
			clearBreakInterval()
		}
	})

	// Cleanup on destroy
	onDestroy(() => {
		clearBreakInterval()
	})

	// Expose breakRemaining for parent component
	export function getBreakRemaining() {
		return breakRemaining
	}
</script>

<div class="break-screen">
	<div class="break-content">
		<h2>Break time</h2>
		<div class="break-countdown">
			<span class="countdown-text">{breakRemaining}</span>
			<span class="countdown-label">seconds remaining</span>
		</div>

		<ProgressBar percentage={(duration - breakRemaining) / duration} />
	</div>
</div>

<style>
	/* Break Screen - centered like scene player video with bottom margin */
	.break-screen {
		width: 100%;
		height: auto;
		padding: 2rem 0;
		display: flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		min-height: 400px;
		max-height: 70vh;
	}

	.break-content {
		text-align: center;
		color: var(--color-text);
	}

	.break-content h2 {
		font-size: var(--h0);
		color: var(--color-text-light);
	}

	.break-content :global(.progress-bar) {
		background-color: var(--color-bg);
	}

	.break-countdown {
		margin-bottom: 2rem;
	}

	.countdown-text {
		font-size: var(--text-h2);
		font-family: var(--font-heading);
		color: var(--color-text);
		line-height: 1;
	}

	.countdown-label {
		font-size: 1rem;
		color: var(--color-text);
		margin-top: 0.5rem;
		display: block;
	}
</style>
